import React, { forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, InputNumber as AntInputNumber } from 'antd';
import { useSelector } from 'react-redux';
import { createDecimalRegExp, DEFAULT_DECIMAL_DIGITS } from 'utils/patterns';
import styled from 'styled-components';
import {
  INTEGER_PATTERN_REPLACE,
  toInteger,
  toPrecision,
} from 'utils/normalizers';
import { CloseCircleFilled } from '@ant-design/icons';

const DEFAULT_MAX_LENGHT = 31;

const InputNumber = forwardRef((_props, ref) => {
  const {
    currency,
    decimal,
    allowNegative,
    precision,
    addonBefore,
    maxLength,
    disabled,
    commas: _commas,
    name,
    ...props
  } = _props;
  const intlFormatter = new Intl.NumberFormat(
    'en-us',
    { style: 'decimal', minimumFractionDigits: decimal ? precision : 0 },
  );
  const moneda = useSelector(({ auth }) => auth.entidad.moneda);
  const commas = _commas || currency;

  const formatter = (input = '') => {
    if (!input) return input;
    let output = intlFormatter.format(input);
    if (!commas) {
      output = output.replace(/,/g, '');
    }
    if (decimal) {
      output = toPrecision(output, precision).toString();
    }
    if (output.includes('.')) {
      output = output.replace(/0+$/, '').replace(/\.$/, '');
    }
    return output;
  };

  const parser = (input = '') => {
    if (!input) return null;
    let [output, outputDecimals = ''] = input.split('.');
    output = toInteger(output.replace(INTEGER_PATTERN_REPLACE, ''), 10);
    outputDecimals = toPrecision(`.${outputDecimals}`, precision);
    output = outputDecimals ? parseFloat(output + outputDecimals) : output;
    return output;
  };

  const formInstance = Form.useFormInstance();
  const value = Form.useWatch(name, formInstance);

  useEffect(() => {
    if (value) {
      const [integer, decimals] = value.toString().split('.');
      if (!decimal) {
        formInstance.setFieldValue(name, integer);
        return;
      }
      if (decimals && decimals.length > precision) {
        formInstance.setFieldValue(name, toPrecision(value, precision));
      }
    }
  }, [value]);

  return (
    <StyledInputNumber
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      formatter={formatter}
      parser={parser}
      addonBefore={addonBefore || (currency ? moneda.simbolo : undefined)}
      min={allowNegative ? undefined : 0}
      style={{ width: '100%' }}
      $right={commas}
      maxLength={maxLength}
      ref={ref}
      disabled={disabled}
      stringMode
    />
  );
});

const StyledInputNumber = styled(AntInputNumber)`
  ${(props) => props.$right && `
    input {
      text-align: end;
      padding-right: 30px !important;
    }
  `}
`;

InputNumber.propTypes = {
  currency: PropTypes.bool,
  decimal: PropTypes.bool,
  allowNegative: PropTypes.bool,
  precision: PropTypes.number,
  addonBefore: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  maxLength: PropTypes.number,
  commas: PropTypes.bool,
  disabled: PropTypes.bool,
  name: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]).isRequired,
};

InputNumber.defaultProps = {
  currency: false,
  decimal: false,
  allowNegative: false,
  precision: 2,
  addonBefore: undefined,
  maxLength: DEFAULT_MAX_LENGHT,
  commas: false,
  disabled: false,
};

const NumericInput = forwardRef((_props, ref) => {
  const {
    name,
    label,
    rules,
    onChange,
    required: _required,
    maxLength: _maxLength,
    minLength: _minLength,
    disabled,
    decimal,
    allowZero,
    tooltip,
    hidden,
    allowNegative,
    addonBefore,
    commas,
    validateTrigger,
    currency,
    precision,
    min,
    max,
    allowClear,
    ...props
  } = _props;
  const form = allowClear ? Form.useFormInstance() : undefined;
  const value = allowClear ? Form.useWatch(name, form) : undefined;
  const moneda = useSelector(({ auth }) => auth.entidad.moneda);
  const maxLength = _maxLength > DEFAULT_MAX_LENGHT ? DEFAULT_MAX_LENGHT : _maxLength;
  const minLength = _minLength < 0 ? 0 : _minLength;
  const required = _required || !!minLength;
  const [decimalRegExp] = createDecimalRegExp(
    currency ? moneda : null,
    { post: precision, allowNegative },
  );
  const integerRegExp = new RegExp(`^${allowNegative ? '-?' : ''}\\d{${minLength},${maxLength}}$`);

  const defaultRule = [
    ...rules,
    { required },
    {
      validator: async (_, _val = '') => {
        const equals = minLength === maxLength;
        const _value = _val?.toString() || '';
        const invalid = decimal ? !decimalRegExp.test(_value) : !integerRegExp.test(_value);
        if (!_value) return;
        if (_value.length < minLength) throw new Error(`El campo debe tener ${!equals ? 'mínimo' : ''} ${minLength} dígitos`);
        if (_value.length > maxLength) throw new Error(`El campo debe tener ${!equals ? 'máximo' : ''} ${maxLength} dígitos`);
        const parsedValue = decimal ? parseFloat(_value) : parseInt(_value, 10);
        if (invalid) throw new Error(`Ingrese un número ${decimal ? 'decimal' : 'entero'} válido`);
        if (!allowZero && parsedValue === 0) throw new Error('No puede ingresar 0');
      },
    },
  ];

  return (
    <FormItemRelativeWrapper>
      <Form.Item
        name={name}
        label={label}
        rules={defaultRule}
        hasFeedback={false}
        tooltip={tooltip}
        hidden={hidden}
        validateTrigger={validateTrigger}
      >
        <InputNumber
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          disabled={disabled}
          onChange={onChange}
          currency={currency}
          decimal={decimal}
          allowNegative={allowNegative}
          precision={precision || (currency ? moneda.decimales : null)}
          addonBefore={addonBefore}
          maxLength={maxLength < DEFAULT_MAX_LENGHT ? maxLength : DEFAULT_MAX_LENGHT}
          commas={commas}
          name={name}
          min={min}
          max={max}
          ref={ref}
        />
      </Form.Item>
      {allowClear && !disabled && !!value && (
        <CloseCircleFilled
          onClick={(e) => {
            e.stopPropagation();
            form.resetFields([name]);
          }}
        />
      )}
    </FormItemRelativeWrapper>
  );
});

const FormItemRelativeWrapper = styled.div`
  position: relative;
  width: 100%;
  .anticon-close-circle {
    position: absolute;
    z-index: 2;
    right: 10px;
    top: 32px;
    color: #00000040 !important;
    font-size: 12px;
    :hover {
      color: #00000073 !important;
    }
  }
`;

NumericInput.propTypes = {
  name: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]).isRequired,
  label: PropTypes.string,
  rules: PropTypes.arrayOf(PropTypes.shape({})),
  onChange: PropTypes.func,
  required: PropTypes.bool,
  maxLength: PropTypes.number,
  disabled: PropTypes.bool,
  minLength: PropTypes.number,
  decimal: PropTypes.bool,
  allowZero: PropTypes.bool,
  hidden: PropTypes.bool,
  tooltip: PropTypes.oneOfType([
    PropTypes.shape({
      title: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.node,
      ]),
    }),
    PropTypes.string,
    PropTypes.number,
  ]),
  allowNegative: PropTypes.bool,
  addonBefore: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  commas: PropTypes.bool,
  validateTrigger: PropTypes.string,
  currency: PropTypes.bool,
  precision: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  allowClear: PropTypes.bool,
};

NumericInput.defaultProps = {
  rules: [],
  onChange: null,
  required: false,
  maxLength: DEFAULT_MAX_LENGHT,
  disabled: false,
  label: '',
  minLength: 0,
  decimal: false,
  allowZero: false,
  hidden: false,
  tooltip: null,
  allowNegative: false,
  addonBefore: null,
  commas: false,
  currency: false,
  validateTrigger: 'onChange',
  precision: DEFAULT_DECIMAL_DIGITS,
  min: null,
  max: null,
  allowClear: true,
};

export default NumericInput;
